import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from "@ngrx/signals";
import { computed , inject } from "@angular/core";
import {
  MobilityQuestionnaire,
  QuestionnaireScore,
  QuestionnaireStatus,
  QuestionnaireSubmissionInterface,
} from "../../core/models/mobility-questionnaire";

import { QuestionnaireService } from "src/app/features/services/questionnaire/questionnaire-service";
import { QuestionnaireAnswerExtractor } from "src/app/features/services/questionnaire/questionnaire-answer-extractor";

interface QuestionnaireState {
  questionnaire: MobilityQuestionnaire | null;
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isCreating: boolean;
  isDirty: boolean;
  error: string | null;
  sectionAnswers: {
    [sectionIndex: number]: { [questionIndex: number]: string };
  };
  score?: QuestionnaireScore | null;
  maturityHasChanged?: boolean;
  isModalMaturityOpen?: boolean;
}

const initialState: QuestionnaireState = {
  questionnaire: null,
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  isCreating: false,
  isDirty: false,
  error: null,
  sectionAnswers: {},
  score: null,
  maturityHasChanged: false,
  isModalMaturityOpen: false,
};

export const QuestionnaireStore = signalStore(
  { providedIn: "root", protectedState: false },
  withState(initialState),
  withMethods((store) => {
    const questionnaireService = inject(QuestionnaireService);

    return {
      setQuestionnaire(questionnaire: MobilityQuestionnaire | null): void {
        const sectionAnswersMap = questionnaire
          ? QuestionnaireAnswerExtractor.extractAnswersMap(questionnaire)
          : {};

        patchState(store, {
          questionnaire,
          sectionAnswers: sectionAnswersMap,
          isLoading: false,
          isCreating: false,
          error: null,
          isDirty: false,
          score: questionnaire?.score ?? null,
        });
      },

      setSectionAnswers(
        sectionIndex: number,
        answers: { [questionIndex: number]: string }
      ): void {
        const currentAnswers = store.sectionAnswers();
        patchState(store, {
          sectionAnswers: {
            ...currentAnswers,
            [sectionIndex]: answers,
          },
          isDirty: true,
        });
      },

      getSectionAnswers(sectionIndex: number): {
        [questionIndex: number]: string;
      } {
        return store.sectionAnswers()[sectionIndex] || {};
      },

      setError(error: string | null): void {
        patchState(store, { error });
      },

      setDirty(isDirty: boolean): void {
        patchState(store, { isDirty });
      },

      setMaturityHasChanged(hasChanged: boolean): void {
        patchState(store, { maturityHasChanged: hasChanged });
      },

      setIsModalMaturityOpen(isOpen: boolean): void {
        patchState(store, { isModalMaturityOpen: isOpen });
      },

      async loadQuestionnaire(): Promise<void> {
        patchState(store, { isLoading: true, error: null });
        try {
          const questionnaire = await questionnaireService.getQuestionnaire();
          this.setQuestionnaire(questionnaire);
        } catch (error) {
          const errorMsg =
            error instanceof Error
              ? error.message
              : "Erreur au chargement du questionnaire";
          patchState(store, { error: errorMsg, isLoading: false });
        }
      },

      async saveSubmission(
        submission: QuestionnaireSubmissionInterface
      ): Promise<void> {
        patchState(store, { isSaving: true, error: null });
        try {
          const result = await questionnaireService.saveSubmission(submission);
          const score = result.score;
          patchState(store, { score: score });
          if (result.completedAt && store.questionnaire()) {
            const updated = new MobilityQuestionnaire({
              id: store.questionnaire()!.id,
              createdAt: store.questionnaire()!.createdAt,
              completedAt: new Date(result.completedAt),
              updatedAt: new Date(),
              state: store.questionnaire()!.state,
              sections: store.questionnaire()!.sections,
            });
            patchState(store, {
              questionnaire: updated,
              isSaving: false,
              isDirty: false,
            });
          } else {
            patchState(store, { isSaving: false, isDirty: false });
          }
        } catch (error) {
          const errorMsg =
            error instanceof Error
              ? error.message
              : "Erreur lors de la sauvegarde";
          patchState(store, { error: errorMsg, isSaving: false });
          throw error;
        }
      },

      async createQuestionnaire(): Promise<MobilityQuestionnaire> {
        patchState(store, { isCreating: true, error: null });
        try {
          const questionnaire =
            await questionnaireService.createQuestionnaire();
          this.setQuestionnaire(questionnaire);
          return questionnaire;
        } catch (error) {
          const errorMsg =
            error instanceof Error
              ? error.message
              : "Erreur à la création du questionnaire";
          patchState(store, { error: errorMsg, isCreating: false });
          throw error;
        }
      },

      getQuestionnaireState(): QuestionnaireStatus {
        const questionnaire = store.questionnaire();
        if (!questionnaire) {
          return QuestionnaireStatus.New;
        }
        return questionnaire.state;
      },

      async deleteQuestionnaire(): Promise<void> {
        patchState(store, { isDeleting: true, error: null });
        try {
          await questionnaireService.deleteQuestionnaire();
          patchState(store, {
            questionnaire: null,
            isDeleting: false,
            sectionAnswers: {},
            error: null,
          });
        } catch (error) {
          const errorMsg =
            error instanceof Error
              ? error.message
              : "Erreur lors de la suppression";
          patchState(store, { error: errorMsg, isDeleting: false });
          throw error;
        }
      },
    };
  }),
  withComputed((store) => ({
    isNewMode: computed(
      () => store.questionnaire()?.state === QuestionnaireStatus.New
    ),

    hasQuestionnaire: computed(() => store.questionnaire() !== null),

    isQuestionnaireLoaded: computed(() => {
      const q = store.questionnaire();
      return q !== null && !q.isEmpty();
    }),

    questionnaireScore: computed(() => {
      const q = store.questionnaire();
      return q?.score;
    }),

    maturityLevel: computed(() => {
      const q = store.questionnaire();
      return q?.score?.maturity;
    }),

    isReadOnly: computed(() => {
      const q = store.questionnaire();
      return q?.state === QuestionnaireStatus.Obsolete;
    }),
  }))
);
